import { apiService } from '@/services/api';
import { createTimestamp } from '@prism-ehr/shared';
import { usesCasesForSpecialty } from '@prism-ehr/shared/utils/caseUtils';
import { selectCurrentPoc } from '@prism-ehr/shared/utils/planOfCareStatus';
import { inferBodyRegionsFromICD10, inferFunctionalAreasFromICD10 } from '@prism-ehr/shared/constants';
import { enrichSectionsData } from '@/utils/noteEnrichment';
import { isTestPatient, resolveTemplateDraft } from '@/utils/templateTesting';

async function fetchICDCodes(patientId, caseId, providerId) {
  // Case-linked: the case is authoritative — seed from case codes or start empty
  if (caseId) {
    try {
      const caseData = await apiService.getCase(caseId);
      if (caseData?.icd10_codes?.length > 0) return caseData.icd10_codes;
    } catch { /* start empty */ }
    return [];
  }
  // No case: carry forward only from this provider's own last signed note
  try {
    const notesRes = await apiService.getClinicalNotes({ patient_id: patientId, provider_id: providerId, signed: true, limit: 1 });
    if (notesRes?.items?.[0]?.icd10_codes?.length > 0) return notesRes.items[0].icd10_codes;
  } catch { /* start empty */ }
  return [];
}

function buildSkeletonFromTemplate(fullTemplate) {
  if (!fullTemplate?.sections?.length) return undefined;
  return {
    templateId: fullTemplate.id,
    templateVersion: fullTemplate.version || 1,
    sections: fullTemplate.sections.map(section => ({
      sectionId: section.id,
      sectionName: section.name,
      fields: (section.fields || []).map(field => ({
        fieldId: field.id,
        value: field.type === 'checkbox' ? false
          : (field.type === 'checkbox_group' || field.type === 'data_grid') ? []
          : ''
      }))
    }))
  };
}

/**
 * Resolve the case for a scribe-created visit. The appointment's case wins;
 * otherwise, in cases-enabled specialties, use the patient's single active
 * case matching the provider's specialty. Zero or multiple candidates →
 * leave unset (never guess between cases).
 */
async function resolveCaseId(appointment, user) {
  if (appointment.case_id) return appointment.case_id;

  if (!usesCasesForSpecialty(user?.organization?.settings, user?.clinical_specialty)) {
    return undefined;
  }

  try {
    const casesRes = await apiService.getCases({ patient_id: appointment.patient_id, status: 'active' });
    let cases = casesRes?.items || casesRes || [];
    if (user?.clinical_specialty) {
      cases = cases.filter(c => !c.clinical_specialty || c.clinical_specialty === user.clinical_specialty);
    }
    if (cases.length === 1) return cases[0].id;
  } catch { /* leave unset */ }

  return undefined;
}

/**
 * Derive body regions / functional areas for templates that use them,
 * mirroring CreateVisitModal.computeRegionPreselections with an ICD-10
 * inference fallback (no picker exists in the silent flow):
 * case → recent same-case encounters → inferred from ICD-10 codes.
 */
async function deriveRegions(fullTemplate, { patientId, caseId, caseData, icd10Codes }) {
  const result = { bodyRegions: [], functionalAreas: [] };
  if (!fullTemplate?.uses_body_regions && !fullTemplate?.uses_functional_areas) return result;

  let recentEncounterItems = null;
  const getRecentEncounters = async () => {
    if (recentEncounterItems) return recentEncounterItems;
    try {
      const recent = await apiService.getEncounters({ patient_id: patientId, case_id: caseId, limit: 5 });
      recentEncounterItems = recent?.items || [];
    } catch {
      recentEncounterItems = [];
    }
    return recentEncounterItems;
  };

  if (fullTemplate.uses_body_regions) {
    if (caseData?.body_regions?.length > 0) {
      result.bodyRegions = caseData.body_regions;
    } else if (caseId) {
      const items = await getRecentEncounters();
      const withRegions = items.find(e => e.body_regions?.length > 0);
      if (withRegions) result.bodyRegions = withRegions.body_regions;
    }
    if (result.bodyRegions.length === 0 && icd10Codes?.length > 0) {
      try { result.bodyRegions = inferBodyRegionsFromICD10(icd10Codes) || []; } catch { /* skip */ }
    }
  }

  if (fullTemplate.uses_functional_areas) {
    if (caseData?.functional_areas?.length > 0) {
      result.functionalAreas = caseData.functional_areas;
    } else if (caseId) {
      const items = await getRecentEncounters();
      const withAreas = items.find(e => e.functional_areas?.length > 0);
      if (withAreas) result.functionalAreas = withAreas.functional_areas;
    }
    if (result.functionalAreas.length === 0 && icd10Codes?.length > 0) {
      try { result.functionalAreas = inferFunctionalAreasFromICD10(icd10Codes) || []; } catch { /* skip */ }
    }
  }

  return result;
}

export async function linkInvoice(appointment, encounterId) {
  // No appointment (quick discharge / case note created from the chart): nothing to link
  if (!appointment) return;
  let invoiceId = appointment.invoice_id;
  if (!invoiceId && appointment.id) {
    try {
      const fresh = await apiService.getAppointment(appointment.id);
      invoiceId = fresh.invoice_id;
    } catch { /* non-blocking */ }
  }
  if (!invoiceId) return;
  // A check-in copay (invoice_type 'copay') is a payment against a patient
  // balance that does not exist yet, not a bill for the visit. It must not
  // occupy encounter.invoice_id (Ready to Bill and overnight claims read that
  // pointer as "billed"); the invoice generator adopts it at billing time
  // through appointment.invoice_id. Any other appointment invoice links as before.
  try {
    const invoice = await apiService.request(`/billing/invoices/${invoiceId}`, { method: 'GET' });
    if (invoice?.invoice_type === 'copay') return;
  } catch (e) {
    console.error('[visitCreation] Invoice lookup failed, linking as before:', e);
  }
  try {
    await Promise.all([
      apiService.updateInvoice(invoiceId, { encounter_id: encounterId, encounter_ids: [encounterId] }),
      apiService.updateEncounter(encounterId, { invoice_id: invoiceId }),
    ]);
  } catch (e) {
    console.error('[visitCreation] Auto-link invoice failed:', e);
  }
}

/**
 * Create the bare encounter for an appointment whose type does not require
 * documentation, link it to the appointment, and auto-link any appointment
 * invoice. This is the single builder for every "Start Visit" / "Mark Complete"
 * path on a no-documentation type — callers must not inline their own payload.
 *
 * Throws on failure; callers decide how to surface the error.
 *
 * @param {Object} appointment - Appointment (with _appointment_type addon when available)
 * @param {Object} user - The current user from UserContext
 * @param {{ status?: string }} [options] - encounter status ('in_progress' default; 'scheduled' for Prepare Note)
 * @returns {Promise<Object>} the created encounter
 */
export async function createBareEncounter(appointment, user, { status = 'in_progress' } = {}) {
  let caseData = null;
  if (appointment.case_id) {
    try { caseData = await apiService.getCase(appointment.case_id); } catch (e) { /* proceed without case data */ }
  }
  const encounterData = {
    patient_id: appointment.patient_id,
    provider_id: appointment.provider_id,
    organization_id: user.organization_id,
    appointment_id: appointment.id,
    encounter_date: createTimestamp(appointment.appointment_date, appointment.start_time),
    encounter_type: appointment.appointment_type,
    status,
    reason_for_visit: appointment.visit_reason,
    location: appointment.is_telehealth ? 'Telehealth' : 'Office',
    location_id: appointment.location_id,
    ...(appointment.case_id && { case_id: appointment.case_id }),
    appointment_type_id: appointment.appointment_type_id || appointment._appointment_type?.id || undefined,
    duration_minutes: appointment.duration_minutes || appointment._appointment_type?.duration_minutes || 30,
    clinical_specialty: user?.clinical_specialty || undefined,
    ...(caseData?.body_regions?.length > 0 && { body_regions: caseData.body_regions }),
    ...(caseData?.functional_areas?.length > 0 && { functional_areas: caseData.functional_areas }),
  };
  const newEncounter = await apiService.createEncounter(encounterData);
  await apiService.updateAppointment(appointment.id, { encounter_id: newEncounter.id });
  await linkInvoice(appointment, newEncounter.id);
  return newEncounter;
}

/**
 * Silently create a visit (encounter + clinical note) for an appointment,
 * mirroring the CreateVisitModal flow without any user interaction.
 *
 * The note is fully enriched (carryforward → form population → prefill) at
 * creation time — scribe-created notes are opened via plain links with no
 * router state, so ClinicalNoteEdit's isNewNote-gated enrichment never runs
 * for them. Enrichment here is idempotent with the editor's own triggers.
 *
 * @param {Object} appointment - The appointment object (with _appointment_type addon)
 * @param {Object} user - The current user from UserContext
 * @param {Object} template - The FULL template object (with sections) — callers
 *   must fetch it by ID (apiService.getTemplate); bare {id} objects are no
 *   longer supported (they silently degraded note_type to 'soap')
 * @returns {Promise<{ encounter: Object, clinicalNote: Object }>}
 */
export async function createVisitSilently(appointment, user, template) {
  if (!template?.id) {
    throw new Error('A template is required to create a scribe visit');
  }

  // Tolerate a sections-less template object by fetching the full one —
  // but the note_type degradation of the old {id}-only path is gone: if the
  // full template can't be loaded, creation fails loudly.
  let fullTemplate = template;
  if (!fullTemplate.sections?.length) {
    fullTemplate = await apiService.getTemplate(template.id);
    if (!fullTemplate?.sections?.length) {
      throw new Error('Could not load the note template for this appointment type');
    }
  }

  // Test patient documents against a template's unpublished draft when one exists
  fullTemplate = resolveTemplateDraft(fullTemplate, isTestPatient(appointment.patient_id, user));

  // 1. Check in the appointment
  try {
    await apiService.updateAppointment(appointment.id, { status: 'checked_in', checked_in_at: Date.now() });
  } catch (err) {
    console.error('[visitCreation] Check-in failed (non-blocking):', err);
  }

  // 2. Resolve case, then ICD-10 codes (case is authoritative for codes)
  const caseId = await resolveCaseId(appointment, user);
  const icd10Codes = await fetchICDCodes(appointment.patient_id, caseId, user.id);

  // 3. Build the skeleton and enrich it (carryforward → form pop → prefill).
  // Enrichment failures fall back to the plain skeleton — visit creation
  // must never fail because a prefill source was unavailable.
  const skeleton = buildSkeletonFromTemplate(fullTemplate);
  let sectionsData = skeleton;
  let caseData = null;
  let regions = { bodyRegions: [], functionalAreas: [] };

  if (skeleton) {
    try {
      const [patientData, fetchedCase, pocResult] = await Promise.all([
        apiService.getPatient(appointment.patient_id).catch(() => null),
        caseId ? apiService.getCase(caseId).catch(() => null) : Promise.resolve(null),
        caseId ? apiService.getPlansOfCare({ case_id: caseId }).catch(() => null) : Promise.resolve(null),
      ]);
      caseData = fetchedCase;

      const activePoc = selectCurrentPoc(
        Array.isArray(pocResult) ? pocResult : pocResult?.items || []
      );

      regions = await deriveRegions(fullTemplate, {
        patientId: appointment.patient_id,
        caseId,
        caseData,
        icd10Codes,
      });

      sectionsData = await enrichSectionsData({
        template: fullTemplate,
        sectionsData: skeleton,
        note: {
          patient_id: appointment.patient_id,
          case_id: caseId,
          note_type: fullTemplate.note_type,
        },
        encounter: { case_id: caseId, appointment_id: appointment.id },
        prefillContext: {
          encounter: { case_id: caseId, appointment_id: appointment.id, encounter_date: appointment.start_timestamp },
          appointment,
          patient: patientData,
          case: caseData,
          plan_of_care: activePoc,
        },
        bodyRegions: regions.bodyRegions,
        functionalAreas: regions.functionalAreas,
        orgSettings: user?.organization?.settings,
      });
    } catch (err) {
      console.error('[visitCreation] Enrichment failed — using plain skeleton:', err);
      sectionsData = skeleton;
    }
  }

  // 4. Create visit atomically
  const encounterTimestamp = appointment.start_timestamp
    ? new Date(appointment.start_timestamp).getTime()
    : createTimestamp(appointment.appointment_date, appointment.start_time);

  const visitData = {
    patient_id: appointment.patient_id,
    provider_id: user.id,
    organization_id: user.organization?.id,
    encounter_date: encounterTimestamp,
    encounter_type: appointment.appointment_type || undefined,
    visit_type: appointment.is_telehealth ? 'telehealth' : 'office',
    appointment_id: appointment.id,
    duration_minutes: appointment.duration_minutes || appointment._appointment_type?.duration_minutes || 30,
    case_id: caseId || undefined,
    clinical_specialty: user?.clinical_specialty || undefined,
    appointment_type_id: appointment.appointment_type_id || appointment._appointment_type?.id || undefined,
    location_id: appointment.location_id || user?.location_id || undefined,
    note_type: fullTemplate.note_type || 'soap',
    template_id: fullTemplate.id,
    reason_for_visit: appointment.visit_reason || undefined,
    body_regions: regions.bodyRegions.length > 0 ? regions.bodyRegions : undefined,
    functional_areas: regions.functionalAreas.length > 0 ? regions.functionalAreas : undefined,
    sections_data: sectionsData || undefined,
    icd10_codes: icd10Codes.length > 0 ? icd10Codes : undefined,
    is_telehealth: appointment.is_telehealth || false,
  };

  const result = await apiService.createVisit(visitData);

  // 5. Link invoice if exists
  await linkInvoice(appointment, result.encounter.id);

  return { encounter: result.encounter, clinicalNote: result.clinical_note };
}
